export const MAX_LOCAL_PDF_BYTES = 250 * 1024 * 1024;
export const LARGE_FILE_WARNING_BYTES = 900 * 1024 * 1024;
export const CAUTION_BYTES = Math.round(MAX_LOCAL_PDF_BYTES * 0.65);

export type FileRisk = "safe" | "caution" | "blocked";

export function classifyFileRisk(fileBytes: number): FileRisk {
  if (fileBytes > MAX_LOCAL_PDF_BYTES) return "blocked";
  return fileBytes > CAUTION_BYTES ? "caution" : "safe";
}

export class StorageQuotaError extends Error {
  constructor(message = "Browser storage quota was reached.") {
    super(message);
    this.name = "StorageQuotaError";
  }
}

export function estimatePeakBytes(fileBytes: number, multiplier = 4) {
  return Math.ceil(fileBytes * multiplier);
}

export function assertFileBudget(fileBytes: number, label = "PDF") {
  if (fileBytes >= LARGE_FILE_WARNING_BYTES) {
    throw new Error(
      `${label} is ${Math.round(fileBytes / 1024 / 1024)} MB; browser-only editing is not safe at this size. Try a smaller file.`,
    );
  }
  if (fileBytes > MAX_LOCAL_PDF_BYTES) {
    throw new Error(
      `${label} is larger than the safe local limit of ${Math.round(MAX_LOCAL_PDF_BYTES / 1024 / 1024)} MB.`,
    );
  }
  const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (deviceMemory && estimatePeakBytes(fileBytes) > deviceMemory * 1024 ** 3 * 0.35) {
    throw new Error(`${label} may exceed available browser memory. Try a smaller file or close other tabs.`);
  }
}

export async function assertStorageBudget(bytesToWrite: number) {
  if (!navigator.storage?.estimate) return;
  const estimate = await navigator.storage.estimate();
  if (estimate.quota && estimate.usage && estimate.usage + bytesToWrite > estimate.quota * 0.85) {
    throw new StorageQuotaError("Not enough browser storage remains for this local session.");
  }
}

export type StorageSnapshot = {
  usage: number;
  quota: number;
  percent: number;
};

export async function readStorageSnapshot(): Promise<StorageSnapshot> {
  if (!navigator.storage?.estimate) return { usage: 0, quota: 0, percent: 0 };
  const estimate = await navigator.storage.estimate();
  const usage = estimate.usage ?? 0;
  const quota = estimate.quota ?? 0;
  return { usage, quota, percent: quota ? Math.min(100, (usage / quota) * 100) : 0 };
}
